import React, { FC, useEffect, memo } from 'react';

import { ModalProps, ModalTypes } from 'application/providers/ModalProvider';
import usePlayerChangeController from 'application/hooks/usePlayerChangeController';
import AnalyticsService, {
  StreamBindFlowEventNameAMP,
  StreamBindFlowParamNameAMP,
} from 'application/services/AnalyticsService';
import { PointParamNameAMP } from 'application/services/AnalyticsService/ampTypes';
import { useBillingInfo } from '@entities/billing';
import { useAccountCompanyId } from 'application/hooks';

import { DeviceTypes, PaymentKind } from '@shared/api/graphql/generated';

import Input from '@zvuk-b2b/react-uikit/ui/Input';
import Button, { ButtonType } from '@zvuk-b2b/react-uikit/ui/Button';
import ModalScreen from '@zvuk-b2b/react-uikit/ui/ModalScreen';

import { TEXTS } from './texts';
import {
  FLOWS_FOR_NOT_SHOW_STOP_ONBOARDING_WARNING,
  FLOWS_FOR_SHOW_DEVICE_BIND_SUCCESS,
  VIDEO_FLOWS,
} from '../../consts';
import { useOnboardingContext } from '../../useOnboardingContext';
import { useOnboardingActionContext } from '../../useOnboardingActionContext';
import { SelectPlayerFlowType } from '../../types';

import styles from './index.module.scss';

const DEVICE_IMAGE_PATH = '/assets/img/app/bindMobile.png';

const BindMobileModal: FC<ModalProps[ModalTypes.MOBILE_PLAYER_BIND]> = ({ openModal, replaceModal, close }) => {
  const { flowType, currentPoint } = useOnboardingContext();
  const { setInFlow, clearCurrentPoint } = useOnboardingActionContext();
  const companyId = useAccountCompanyId();

  const { hasInactiveLicenses, selectedPaymentKind } = useBillingInfo(companyId);

  const isShowPaymentModal = () => {
    const hasDevice = Boolean(currentPoint?.device?.type);
    const hasStream = Boolean(currentPoint?.stream?.id);

    if (selectedPaymentKind === PaymentKind.Sbbol) {
      return hasInactiveLicenses && hasDevice && hasStream;
    }

    return currentPoint?.isEnabled === false && hasDevice && hasStream;
  };

  const onSuccessCallback = (deviceType: DeviceTypes) => {
    AnalyticsService.event(StreamBindFlowEventNameAMP.STREAM_PLAYER_TYPE_BOUND, {
      [StreamBindFlowParamNameAMP.PLAYER_TYPE]: deviceType,
      [PointParamNameAMP.POINT_ID]: currentPoint?.id,
    });

    setInFlow(false);

    if ((flowType && FLOWS_FOR_SHOW_DEVICE_BIND_SUCCESS.has(flowType)) || currentPoint?.isVideoDemo) {
      replaceModal(ModalTypes.DEVICE_BIND_SUCCESS, {});

      return;
    }

    if (
      isShowPaymentModal() &&
      (flowType === SelectPlayerFlowType.FULL_ONBOARDING ||
        flowType === SelectPlayerFlowType.CREATE_POINT_WHILE_BINDING_STREAM)
    ) {
      replaceModal(ModalTypes.PAYMENT_REQUIREMENT, {});
    } else {
      replaceModal(
        flowType === SelectPlayerFlowType.BIND_STREAM ||
          flowType === SelectPlayerFlowType.CREATE_POINT_WHILE_BINDING_STREAM
          ? ModalTypes.STREAM_BIND_SUCCESS
          : ModalTypes.DEVICE_BIND_SUCCESS,
        {}
      );
    }
  };

  const { bindingCode, setBindingCode, bindingCodeError, handleUpdateClick, updateDeviceLoading, setToPlayerType } =
    usePlayerChangeController({
      pointId: currentPoint?.id || '',
      fromPlayerType: DeviceTypes.Ios, // TODO: don't matter which mobile type will be passed
      isVideoMode: flowType && VIDEO_FLOWS.has(flowType),
      onSuccessCallback,
    });

  useEffect(() => {
    setToPlayerType('APP');
  }, []);

  const handleCancel = () => {
    if (flowType && FLOWS_FOR_NOT_SHOW_STOP_ONBOARDING_WARNING.has(flowType)) {
      close();
      clearCurrentPoint();
      return;
    }

    openModal(ModalTypes.STREAM_STOP_BINDING_FLOW_WARNING, {});
  };

  return (
    <ModalScreen
      titlePlacement="left"
      onClose={handleCancel}
      title={TEXTS.MODAL_TITLE}
      className={styles.MobilePlayerFlowModal}
      contentClassName={styles.MobilePlayerFlowModalContent}
      containerActionButtonsClassName={styles.MobilePlayerFlowModalActionButtons}
      actionButtons={[
        <Button
          key="button_mobile_player_modal_bind_success"
          elementId="button_mobile_player_modal_bind_success"
          type={ButtonType.PRIMARY}
          isDisabled={bindingCode.length < 4}
          isLoading={updateDeviceLoading}
          onClick={handleUpdateClick}>
          {TEXTS.SUCCESS_BUTTON}
        </Button>,
        <Button
          key="button_mobile_player_modal_bind_cancel"
          elementId="button_mobile_player_modal_bind_cancel"
          type={ButtonType.SECONDARY}
          isLoading={updateDeviceLoading}
          onClick={() => {
            replaceModal(ModalTypes.MOBILE_PLAYER_DOWNLOAD, {});
          }}>
          {TEXTS.CANCEL_BUTTON}
        </Button>,
      ]}>
      <div className={styles.MobilePlayerFlowModalDescription}>{TEXTS.TITLE_DESCRIPTION}</div>

      <div className={styles.MobilePlayerFlowModalDownloadContent}>
        <Input
          elementId="button_mobile_player_modal_bind_code_input"
          onChange={setBindingCode}
          value={bindingCode}
          placeholder={TEXTS.CODE_BIND_PLACEHOLDER}
          maxLength={4}
          label={TEXTS.CODE_BIND_TITLE}
          error={bindingCodeError}
          containerClassName={styles.MobilePlayerFlowModalInput}
        />

        <img
          src={DEVICE_IMAGE_PATH}
          alt=""
          loading="lazy"
          className={styles.MobilePlayerFlowModalImageBind}
        />
      </div>
    </ModalScreen>
  );
};

export default memo(BindMobileModal);
